import { useApolloClient, useFragment } from '@provision/graphql-client';
import { useEffect, useState } from 'react';
import {
  GET_MESSAGE_THREAD_QUERY,
  MESSAGE_THREAD_FRAGMENT,
} from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.operations';
import type { MessageThreadData } from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.types';

export enum TargetReplyStatus {
  /** The target is a timeline message, not a reply. */
  NotReply = 'not-reply',
  Shown = 'shown',
  Refreshing = 'refreshing',
  Missing = 'missing',
}

enum ThreadRefreshStatus {
  Pending = 'pending',
  HasReply = 'has-reply',
  LacksReply = 'lacks-reply',
}

interface ThreadRefresh {
  targetMessageId: string;
  status: ThreadRefreshStatus;
  repliesBefore: ThreadReplies;
}

type ThreadReplies = MessageThreadData['replies'] | undefined;

interface TargetReplyThreadInput {
  targetMessageId: string | null;
  timelineMessageId: string | null;
  isTimelineMessageRendered: boolean;
}

function hasLiveReply(replies: ThreadReplies, replyId: string): boolean {
  return !!replies?.some((reply) => reply.id === replyId && !reply.deletedAt);
}

/*
  Whether a target reply is in the cached thread of its note. A note rendered from a page
  cached before the reply arrived shows the thread without it — the live thread subscription
  only runs while the conversation is open — so the thread is refetched once. The refetch's
  own result decides: it settles on the cache write, before the thread re-renders with it.
*/
export function useTargetReplyThread({
  targetMessageId,
  timelineMessageId,
  isTimelineMessageRendered,
}: TargetReplyThreadInput): TargetReplyStatus {
  const client = useApolloClient();
  const isReply =
    !!targetMessageId && !!timelineMessageId && timelineMessageId !== targetMessageId;

  const fragment = useFragment<MessageThreadData>({
    fragment: MESSAGE_THREAD_FRAGMENT,
    fragmentName: 'MessageThread',
    from: { __typename: 'ConversationMessage', id: timelineMessageId ?? '' },
  });
  const replies: ThreadReplies = fragment.complete ? fragment.data.replies : undefined;
  const isShown = isReply && hasLiveReply(replies, targetMessageId);

  const [refreshRequest, setRefreshRequest] = useState<ThreadRefresh>();
  const refresh = refreshRequest?.targetMessageId === targetMessageId ? refreshRequest : null;
  const shouldRefresh = isReply && isTimelineMessageRendered && !isShown && !refresh;

  useEffect(() => {
    if (!shouldRefresh || !targetMessageId || !timelineMessageId) return;
    setRefreshRequest({
      targetMessageId,
      status: ThreadRefreshStatus.Pending,
      repliesBefore: replies,
    });

    const refetchThread = async () => {
      let status = ThreadRefreshStatus.LacksReply;
      try {
        const { data } = await client.query<{
          getConversationMessage: { records: MessageThreadData[] };
        }>({
          query: GET_MESSAGE_THREAD_QUERY,
          variables: {
            params: {
              filters: [
                {
                  attribute: 'conversationMessageId',
                  operator: '=',
                  value: timelineMessageId,
                },
              ],
              limit: 1,
            },
          },
          fetchPolicy: 'network-only',
          context: { ttl: 0 },
        });
        const [thread] = data?.getConversationMessage.records ?? [];
        if (hasLiveReply(thread?.replies, targetMessageId)) {
          status = ThreadRefreshStatus.HasReply;
        }
      } catch {
        // a failed refetch leaves the reply out of reach, which the not-found toast reports
      }
      setRefreshRequest((prev) =>
        prev?.targetMessageId === targetMessageId ? { ...prev, status } : prev,
      );
    };
    void refetchThread();
  }, [shouldRefresh, targetMessageId, timelineMessageId, client, replies]);

  if (!isReply) return TargetReplyStatus.NotReply;
  if (isShown) return TargetReplyStatus.Shown;

  switch (refresh?.status) {
    case ThreadRefreshStatus.LacksReply:
      return TargetReplyStatus.Missing;
    case ThreadRefreshStatus.HasReply:
      return replies === refresh.repliesBefore
        ? TargetReplyStatus.Refreshing
        : TargetReplyStatus.Missing;
    default:
      return TargetReplyStatus.Refreshing;
  }
}
